import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { TrialCard } from "@/components/Trial";
import { CurrentPlan } from "@/components/billing/CurrentPlan";
import { PlanCatalog } from "@/components/billing/PlanCatalog";
import { BillingDialogs } from "@/components/billing/BillingDialogs";
import { OwnerAccessCard } from "@/components/billing/OwnerAccessCard";

export default function Billing() {
  const { activeWorkspaceId, currentRole } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [dlg, setDlg] = useState(null); // {type: 'pause'|'cancel'|'change'|'downgrade', plan}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const admin = currentRole === "admin";

  const load = async () => {
    const [b, p] = await Promise.all([api.get(`/workspaces/${activeWorkspaceId}/billing`), api.get("/billing/plans")]);
    setData(b.data); setPlans(p.data.plans);
    if (b.data.subscription?.cycle === "annual") setCycle("annual");
    if (admin) api.get(`/workspaces/${activeWorkspaceId}/billing/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(() => { if (activeWorkspaceId) load(); }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (params.get("welcome")) toast.success("Your plan is active. Welcome aboard!"); if (params.get("canceled")) toast.info("Checkout canceled"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (path, body = {}, ok = "Done") => {
    setBusy(true);
    try { const { data: r } = await api.post(`/workspaces/${activeWorkspaceId}/billing/${path}`, body); toast.success(ok); setDlg(null); await load(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const checkout = async (plan_code, cyc) => {
    setBusy(true);
    try { const { data: r } = await api.post(`/workspaces/${activeWorkspaceId}/billing/checkout`, { plan_code, cycle: cyc, origin_url: window.location.origin }); window.location.href = r.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(false); }
  };
  const portal = async () => { const { data: r } = await api.post(`/workspaces/${activeWorkspaceId}/billing/portal`, { origin_url: window.location.origin }); window.location.href = r.url; };

  if (!data) return <div className="p-10 text-slate-400">Loading billing…</div>;
  const { subscription: sub, usage, settings } = data;
  const isRecurring = sub.stripe_subscription_id && sub.cycle !== "one_time";
  const subs = plans.filter((p) => p.kind === "subscription");
  const oneTime = plans.filter((p) => p.kind === "one_time");

  if (sub.status === "platform_owner") {
    return (
      <div className="p-8 max-w-[1400px] mx-auto" data-testid="billing-page">
        <div className="label-cap text-amber-500">// SUBSCRIPTION & BILLING</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1 mb-6 flex items-center gap-2"><CreditCard className="w-6 h-6 text-amber-400" /> Billing</h1>
        <OwnerAccessCard sub={sub} usage={usage} />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="billing-page">
      <div className="label-cap text-amber-500">// SUBSCRIPTION & BILLING</div>
      <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1 mb-6 flex items-center gap-2"><CreditCard className="w-6 h-6 text-amber-400" /> Billing</h1>

      <TrialCard workspaceId={activeWorkspaceId} admin={admin} onChanged={load} />

      {data.show_upsell && (
        <div className="sc-card p-4 mb-4 border-amber-500/50 text-sm" data-testid="single-project-upsell">You've purchased {data.single_project_purchases} project reviews. A <b>Fabricator</b> subscription includes up to {subs[0]?.limits.active_projects} active projects for ${subs[0]?.price_monthly}/month — and keeps this account, its projects, findings and playbooks.
          {admin && <button onClick={() => checkout("fabricator", "monthly")} className="ml-3 text-amber-400 underline" data-testid="upsell-upgrade-button">Upgrade now</button>}</div>
      )}

      <CurrentPlan sub={sub} usage={usage} settings={settings} admin={admin} isRecurring={isRecurring} busy={busy} post={post} portal={portal} setDlg={setDlg} />
      {admin && <PlanCatalog sub={sub} usage={usage} settings={settings} subs={subs} oneTime={oneTime} invoices={invoices} isRecurring={isRecurring} cycle={cycle} setCycle={setCycle} busy={busy} setDlg={setDlg} checkout={checkout} />}
      <BillingDialogs dlg={dlg} setDlg={setDlg} sub={sub} usage={usage} settings={settings} cycle={cycle} busy={busy} post={post} reason={reason} setReason={setReason} />
    </div>
  );
}
